import { useEffect, useMemo, useState } from 'react'
import { useImmer } from 'use-immer'
import { ModalPasswordConfirmation, UiCard, UiCardBody, UiCardHeader, UiDataTable } from 'components/common'
import { Button, Flex, FormControl, FormLabel, IconButton, Input, Select, Text, useDisclosure } from '@chakra-ui/react'
import { getPayoutChangeOrders, getPayoutRateForSalesUsers, getUsersByRoles, runPayoutPayChangeOrders } from 'api'
import { ROLES, getAssetUrl, numberFormatter } from 'lib'
import { format as formatDate } from 'date-fns'
import { ModalChangeOrderCommissionEdit } from './ModalChangeOrderCommissionEdit'
import { MdEdit } from 'react-icons/md'
import { useAppContext } from 'hooks/useAppContext'

const COMMISSION_COLUMNS = [
  {
    label: 'User',
    orderBy: 'userName',
    render: ({ userName }) => userName
  },
  {
    label: 'Order Date',
    orderBy: 'createdAt',
    render: ({ createdAt }) => formatDate(new Date(createdAt), 'MM/dd/yyyy')
  },
  {
    label: 'Change Order Name',
    orderBy: 'name',
    render: ({ name }) => name
  },
  {
    label: 'Price',
    orderBy: 'commissionablePrice',
    render: ({ commissionablePrice }) => '$ ' + numberFormatter(commissionablePrice)
  },
  {
    label: 'Rate',
    orderBy: null,
    render: ({ rate }) => rate + '%'
  },
  {
    label: 'Pay',
    orderBy: null,
    render: ({ id, pay, onEditCommissionAmount }) => {
      return (
        <Flex>
          <Text>$ {numberFormatter(pay)}</Text>
          <IconButton
            variant="ghost"
            size="sm"
            colorScheme="gray"
            icon={<MdEdit fontSize="18px" />}
            onClick={() => onEditCommissionAmount(id, pay)}
          />
        </Flex>
      )
    }
  },
  {
    label: 'Payment Status',
    orderBy: 'commissionPaid',
    render: ({ commissionPaid }) => commissionPaid ? 'Paid' : 'Unpaid'
  }
]

export const CommissionChangeOrders = (props) => {
  const { createToast, currentUser } = useAppContext()
  const [tableConfig, setTableConfig] = useImmer({
    curPage: 1,
    totalPages: 1,
    totalRecords: 0,
    itemsPerPage: 10,
    search: '',
    orderBy: {
      key: 'createdAt',
      direction: 'asc'
    }
  })
  const [isLoading, setIsLoading] = useState(false)
  const [changeOrders, setChangeOrders] = useState([])
  const [filterChangeOrders, setFilterChangeOrders] = useImmer({
    startDate: '2000-01-01',
    endDate: formatDate(new Date(), 'yyyy-MM-dd'),
    userId: 'All'
  })
  const [salesUsers, setSalesUsers] = useState([])
  const [pmUsers, setPmUsers] = useState([])

  const disclosureCommissionEdit = useDisclosure()
  const [selectedChangeOrderForCommissionEdit, setSelectedChangeOrderForCommissionEdit] = useState(null)

  const disclosurePasswordConfirmation = useDisclosure()

  const [allSelected, setAllSelected] = useState(false)
  const [selectedChangeOrders, setSelectedChangeOrders] = useState([])

  useEffect(() => {
    const loadRateForSalesUsers = async () => {
      try {
        const response = await getPayoutRateForSalesUsers()
        setSalesUsers(response)
      } catch (err) {
        console.log(err)
      }
    }
    const loadProjectManagers = async () => {
      try {
        const response = await getUsersByRoles(1, 9999999, '', 'name', 'asc', [ROLES.Admin, ROLES.ProjectManager])
        setPmUsers(response.rows)
      } catch (err) {
        console.log(err)
      }
    }
    loadRateForSalesUsers()
    loadProjectManagers()
  }, [])

  const load = async () => {
    setIsLoading(true)
    try {
      const response = await getPayoutChangeOrders(
        filterChangeOrders.startDate,
        filterChangeOrders.endDate,
        filterChangeOrders.userId,
        tableConfig.curPage,
        tableConfig.itemsPerPage,
        tableConfig.search,
        tableConfig.orderBy.key,
        tableConfig.orderBy.direction
      )
      setChangeOrders(response.rows.map(changeOrder => {
        const rate = salesUsers.filter(user => user.id === changeOrder.salespersonId)?.[0]?.commissionRate || 5
        let pay
        if (changeOrder.commissionManualEdited) {
          pay = changeOrder.commissionManualAmount
        } else {
          pay = changeOrder.commissionablePrice * rate * 0.01
          if (pay < 0) {
            pay = 0
          }
        }
        return { ...changeOrder, rate, pay }
      }))
      setTableConfig(draft => {
        draft.totalPages = response.totalPages
        draft.totalRecords = response.totalRecords
      })
      setIsLoading(false)
    } catch (err) {
      console.log(err)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [salesUsers, tableConfig.curPage, tableConfig.itemsPerPage, tableConfig.search, tableConfig.orderBy.key, tableConfig.orderBy.direction])

  const onEditCommissionAmount = (changeOrderId, pay) => {
    disclosureCommissionEdit.onOpen()
    setSelectedChangeOrderForCommissionEdit({ changeOrderId, pay: parseFloat(pay.toFixed(2)) })
  }

  const onSelect = (e, row) => {
    if (row) {
      setSelectedChangeOrders([...selectedChangeOrders, row])
    } else {
      if (allSelected) {
        setSelectedChangeOrders([])
      }
      setAllSelected(!allSelected)
    }
  }

  const handleChange = (e) => {
    setFilterChangeOrders(draft => {
      draft[e.target.name] = e.target.value
    })
  }

  const onClickRunReport = () => {
    load()
  }

  const onClickPayRun = () => {
    disclosurePasswordConfirmation.onOpen()
  }

  const handlePayRun = async () => {
    try {
      let selectedIds = []
      if (allSelected) {
        const response = await getPayoutChangeOrders(filterChangeOrders.startDate, filterChangeOrders.endDate, filterChangeOrders.userId, 1, 99999999, '', 'createdAt', 'asc')
        selectedIds = response.rows.filter(v => !v.commissionPaid).map(v => v.id)
      } else {
        selectedIds = selectedChangeOrders.map(v => v.id)
      }

      if (!selectedIds.length) {
        createToast('warning', 'Warning', 'No rows selected.')
        return
      }

      const response = await runPayoutPayChangeOrders(false, selectedIds)
      window.open(getAssetUrl(response.pdf))
      createToast('success', 'Success', 'Commission has been paid for selected change orders.')

      load()
      setAllSelected(false)
      setSelectedChangeOrders([])
    } catch (err) {
      createToast('error', 'Error', err.response.data)
    }
    disclosurePasswordConfirmation.onClose()
  }

  const customizedChangeOrders = useMemo(() => {
    return changeOrders.map(v => {
      return { ...v, onEditCommissionAmount, canSelect: !v.commissionPaid }
    })
  }, [changeOrders])

  return (
    <>
      <UiCard borderRadius={0} {...props}>
        <UiCardHeader bg="gray.500" p={4}>
          <Text fontWeight="bold" color="white">Change Orders</Text>
        </UiCardHeader>
        <UiCardBody>
          <Flex gap={4} alignItems="flex-end">
            <FormControl flex={1}>
              <FormLabel>User</FormLabel>
              <Select name="userId" value={filterChangeOrders.userId} onChange={handleChange}>
                <option value="All">All</option>
                {pmUsers.map(user => <option key={user.id} value={user.id}>{user.firstname} {user.lastname}</option>)}
                {salesUsers.map(user => <option key={user.id} value={user.id}>{user.firstname} {user.lastname}</option>)}
              </Select>
            </FormControl>
            <FormControl flex={1}>
              <FormLabel>Order Date</FormLabel>
              <Input type="date" name="startDate" value={filterChangeOrders.startDate} onChange={handleChange} />
            </FormControl>
            <FormControl flex={1}>
              <FormLabel>Thru</FormLabel>
              <Input type="date" name="endDate" value={filterChangeOrders.endDate} onChange={handleChange} />
            </FormControl>
            <Button size="sm" colorScheme="blue" flex={1} onClick={onClickRunReport}>Run Report</Button>
          </Flex>
          {currentUser?.role === ROLES.Admin && (
            <Flex justifyContent="center">
              <Button size="md" colorScheme="blue" onClick={onClickPayRun} mt={8} mb={4}>Add to Pay Run</Button>
            </Flex>
          )}
          <UiDataTable
            tableConfig={tableConfig}
            updateTableConfig={setTableConfig}
            rows={customizedChangeOrders}
            rowKey="id"
            columns={COMMISSION_COLUMNS}
            isLoading={isLoading}
            selected={selectedChangeOrders}
            onSelect={onSelect}
            allSelected={allSelected}
          />
        </UiCardBody>
      </UiCard>
      <ModalChangeOrderCommissionEdit
        changeOrderId={selectedChangeOrderForCommissionEdit?.changeOrderId || null}
        originalAmount={selectedChangeOrderForCommissionEdit?.pay || 0}
        isOpen={disclosureCommissionEdit.isOpen}
        onClose={() => {
          setSelectedChangeOrderForCommissionEdit(null)
          disclosureCommissionEdit.onClose()
        }}
        onSave={() => {
          load()
          disclosureCommissionEdit.onClose()
        }}
      />
      <ModalPasswordConfirmation
        {...disclosurePasswordConfirmation}
        heading={`Add to Pay Run - ${allSelected ? 'All qualified' : selectedChangeOrders.length} rows selected`}
        btnConfirmLabel="Confirm"
        btnCancelLabel="Cancel"
        onConfirm={handlePayRun}
      />
    </>
  )
}
